import { useState } from 'react';

import { useSearchParams } from '@remix-run/react';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { formatDate } from '~/lib/utils';
import { getAuctionHouseListWithoutPagination } from '~/services/api/auction-houses';
import { getCategoryList } from '~/services/api/categories';
import { AuctionType } from '~/types';

import { DatePicker } from './ui/date-picker';

export interface Filters {
  date: Date | undefined;
  auctionTypes: string[];
  categories: string[];
  sellers: string[];
}

interface FilterSideBarProps {
  type: 'auction' | 'product';
  auctionHouses: Awaited<ReturnType<typeof getAuctionHouseListWithoutPagination>>;
  categories: Awaited<ReturnType<typeof getCategoryList>>;
}

export default function FilterSideBar({ type, auctionHouses, categories }: FilterSideBarProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [date, setDate] = useState<Date | undefined>(
    searchParams.has('date') ? new Date(searchParams.get('date')!.replace('+', '/')) : undefined,
  );

  const handleClear = () => {
    setDate(undefined);
    searchParams.delete('date');
    searchParams.delete('type');
    searchParams.delete('category');
    searchParams.delete('seller');
    searchParams.delete('search');
    setSearchParams(searchParams, {
      preventScrollReset: true,
    });
  };

  return (
    <div className="flex flex-col gap-6 border border-slate-200 p-4">
      <div className="flex flex-row items-center justify-between">
        <p>Filter</p>
        <Button variant={'link'} className="p-0 text-xs text-blue-600" onClick={handleClear}>
          Clear
        </Button>
      </div>
      <div className="flex flex-col gap-1">
        <p className="font-regular text-xs text-slate-500">AUCTION DATE</p>
        <DatePicker
          date={date}
          setDate={(date) => {
            setDate(date);
            searchParams.set('date', formatDate(date));
            setSearchParams(searchParams, {
              preventScrollReset: true,
            });
          }}
          placeholder="Pick a date"
        />
      </div>
      <div className="flex flex-col gap-1">
        <p className="font-regular text-xs text-slate-500">AUCTION TYPE</p>
        <div className="flex flex-row items-start space-x-2">
          <Checkbox
            id="live"
            className="mt-1.5 border-slate-400"
            checked={searchParams.has('type', AuctionType.TIME2LIVE)}
            onCheckedChange={(checked) => {
              if (!checked) {
                searchParams.delete('type');
              } else {
                searchParams.set('type', AuctionType.TIME2LIVE);
              }
              setSearchParams(searchParams, {
                preventScrollReset: true,
              });
            }}
          />
          <div>
            <label htmlFor="time2live" className="text-sm font-medium text-slate-900">
              Time2Live
            </label>
            <p className="w-4/5 text-xs text-slate-500">Bid anytime! Auctions end when the timer runs out</p>
          </div>
        </div>
        <div className="flex flex-row items-start space-x-2">
          <Checkbox
            id="online"
            className="mt-1.5 border-slate-400"
            checked={searchParams.has('type', AuctionType.TIMEONLINE)}
            onCheckedChange={(checked) => {
              if (!checked) {
                searchParams.delete('type');
              } else {
                searchParams.set('type', AuctionType.TIMEONLINE);
              }
              setSearchParams(searchParams, {
                preventScrollReset: true,
              });
            }}
          />
          <div>
            <label htmlFor="timeonline" className="text-sm font-medium text-slate-900">
              TimeOnline
            </label>
            <p className="w-4/5 text-xs text-slate-500">
              Bid online until live auction starts. Join the excitement live!
            </p>
          </div>
        </div>
      </div>

      {type === 'product' && (
        <div className="flex flex-col gap-1">
          <p className="font-regular text-xs text-slate-500">CATEGORY</p>
          {categories.list.map((category) => (
            <div key={category.id} className="flex items-center space-x-2">
              <Checkbox
                id={category.id}
                className="border-slate-400"
                checked={searchParams.has('category', category.name)}
                onCheckedChange={(checked) => {
                  if (!checked) {
                    searchParams.delete('category', category.name);
                  } else {
                    searchParams.append('category', category.name);
                  }
                  setSearchParams(searchParams, {
                    preventScrollReset: true,
                  });
                }}
              />
              <label htmlFor="Art" className="text-sm font-medium text-slate-900">
                {category.name}
              </label>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-col gap-1">
        <p className="font-regular text-xs text-slate-500">SELLERS</p>
        {auctionHouses.map((auctionHouse) => (
          <div key={auctionHouse.id} className="flex items-center space-x-2">
            <Checkbox
              id={auctionHouse.slug}
              className="border-slate-400"
              checked={searchParams.has('seller', auctionHouse.slug)}
              onCheckedChange={(checked) => {
                if (!checked) {
                  searchParams.delete('seller');
                } else {
                  searchParams.append('seller', auctionHouse.slug);
                }
                setSearchParams(searchParams, {
                  preventScrollReset: true,
                });
              }}
            />
            <label htmlFor={`${auctionHouse.slug}`} className="text-sm font-medium text-slate-900">
              {auctionHouse.name}
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}
